import { serializeLocalWrite } from './localSaveState'
import { db } from './webStorage'
import type { FontCatalogFamily, FontCatalogManifest } from '../types/fontCatalog'

const NATIVE_FONT_DIR = 'font-catalog/core'

function isTauriRuntime(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window
}

function safeCatalogPath(path: string): string {
  const normalized = path.replace(/\\/g, '/')
  if (!normalized || normalized.startsWith('/') || normalized.split('/').includes('..')) {
    throw new Error(`Unsafe font catalog path: ${path}`)
  }
  return normalized
}

function exactArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}

export async function readCoreFontBytes(path: string): Promise<ArrayBuffer | null> {
  const safePath = safeCatalogPath(path)
  // Keep bytes beside the catalog metadata on every platform. Native AppData
  // filesystem reads can be denied because that directory contains WebView
  // data; IndexedDB already provides app-owned storage without OS installation.
  let stored
  try { stored = await db.fontAssets.get(safePath) }
  catch (cause) { throw new Error(`Cannot read cached font ${safePath}: ${String(cause)}`) }
  if (stored?.bytes?.byteLength) return stored.bytes

  if (isTauriRuntime()) {
    // Reuse readable pre-fix native files. A legacy permission failure is a
    // cache miss: the next download writes to the shared app cache instead.
    try {
      const [{ readFile }, { BaseDirectory }] = await Promise.all([
        import('@tauri-apps/plugin-fs'),
        import('@tauri-apps/api/path'),
      ])
      const bytes = await readFile(`${NATIVE_FONT_DIR}/${safePath}`, { baseDir: BaseDirectory.AppData })
      return bytes.length ? exactArrayBuffer(bytes) : null
    } catch { return null }
  }
  return null
}

function sameBytes(actual: ArrayBuffer | undefined, expected: ArrayBuffer): boolean {
  const target = new Uint8Array(expected)
  return actual?.byteLength === expected.byteLength && new Uint8Array(actual).every((byte, index) => byte === target[index])
}

export async function writeCoreFontBytes(path: string, bytes: ArrayBuffer): Promise<void> {
  const safePath = safeCatalogPath(path)
  return serializeLocalWrite(`offline-font:${safePath}`, async () => {
    if (!bytes.byteLength) throw new Error(`Cannot cache an empty font: ${safePath}`)
    if (sameBytes((await db.fontAssets.get(safePath))?.bytes, bytes)) return
    await db.fontAssets.put({ path: safePath, bytes })
    // Verify the primary cache, not a possible legacy filesystem fallback.
    if (!sameBytes((await db.fontAssets.get(safePath))?.bytes, bytes)) {
      throw new Error(`Cached font verification failed: ${safePath}`)
    }
  })
}

export async function readCachedManifest(): Promise<FontCatalogManifest | null> {
  return (await db.fontCatalogCache.get('manifest'))?.manifest ?? null
}

export async function writeCachedManifest(
  manifest: FontCatalogManifest,
  etag: string | null,
): Promise<void> {
  await db.fontCatalogCache.put({ id: 'manifest', manifest, etag, updatedAt: Date.now() })
}

export async function writeInstalledFamily(family: FontCatalogFamily, offlinePinned = false): Promise<void> {
  // Pinia wraps catalog entries in reactive proxies, which IndexedDB cannot
  // structured-clone. Persist a plain data snapshot instead.
  const plainFamily = JSON.parse(JSON.stringify(family)) as FontCatalogFamily
  await serializeLocalWrite(`offline-font-family:${family.id}`, async () => {
    const previous = await db.installedFontFamilies.get(family.id)
    await db.installedFontFamilies.put({ id: family.id, family: plainFamily, offlinePinned: offlinePinned || previous?.offlinePinned, updatedAt: Date.now() })
  })
}

export async function readInstalledFamilies(ids: readonly string[]): Promise<FontCatalogFamily[]> {
  if (ids.length === 0) return []
  const rows = await db.installedFontFamilies.bulkGet([...ids])
  return rows.flatMap((row) => row?.family ? [row.family] : [])
}

export async function readOfflinePinnedFamilies(): Promise<FontCatalogFamily[]> {
  return (await db.installedFontFamilies.toArray()).filter(row => row.offlinePinned).map(row => row.family)
}
